import { CartAddEvent, CartErrorEvent, ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

class LookBundle extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';

    this.addEventListener('change', this.#handleOptionChange);
    this.querySelector('[data-look-bundle-add]')?.addEventListener('click', this.#handleAdd);
    document.addEventListener(ThemeEvents.variantUpdate, this.#handleMainVariantUpdate);

    this.#items.forEach((item) => this.#syncItem(item));
    this.#updateTotals();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#handleOptionChange);
    this.querySelector('[data-look-bundle-add]')?.removeEventListener('click', this.#handleAdd);
    document.removeEventListener(ThemeEvents.variantUpdate, this.#handleMainVariantUpdate);
  }

  get #items() {
    return Array.from(this.querySelectorAll('[data-look-bundle-item]'));
  }

  #handleOptionChange = (event) => {
    const item = event.target?.closest?.('[data-look-bundle-item]');
    if (!item) return;

    this.#syncItem(item);
    this.#updateTotals();
  };

  #handleMainVariantUpdate = (event) => {
    if (event.detail?.data?.productId !== this.dataset.productId) return;

    const currentItem = this.querySelector('[data-look-bundle-item][data-current="true"]');
    if (!currentItem || !event.detail?.resource?.id) return;

    currentItem.dataset.selectedVariantId = event.detail.resource.id;
    this.#syncItem(currentItem);
    this.#updateTotals();
  };

  #syncItem(item) {
    const variant = this.#getSelectedVariant(item);
    if (!variant) return;

    item.dataset.selectedVariantId = variant.id;
    item.dataset.price = variant.price;
    item.dataset.comparePrice = variant.compare_at_price || variant.price;
    item.dataset.available = variant.available ? 'true' : 'false';

    const image = item.querySelector('[data-look-bundle-image]');
    if (image && variant.featured_image) {
      image.setAttribute('src', variant.featured_image);
    }

    const price = item.querySelector('[data-look-bundle-item-price]');
    if (price) price.textContent = this.#formatMoney(variant.price);

    const compare = item.querySelector('[data-look-bundle-item-compare]');
    if (compare) {
      const comparePrice = variant.compare_at_price || variant.price;
      compare.textContent = this.#formatMoney(comparePrice);
      compare.hidden = comparePrice <= variant.price;
    }
  }

  #getSelectedVariant(item) {
    const variants = this.#getVariants(item);
    if (!variants.length) return null;

    if (item.dataset.current === 'true') {
      const currentVariantId = this.#currentProductVariantId() || item.dataset.selectedVariantId;
      return variants.find((variant) => String(variant.id) === String(currentVariantId)) || variants[0];
    }

    const selectedOptions = Array.from(item.querySelectorAll('[data-look-bundle-option-group]')).map((group) => {
      const selected = group.querySelector('[data-look-bundle-option]:checked');
      return selected?.value || '';
    });

    if (!selectedOptions.length) {
      return variants.find((variant) => variant.available) || variants[0];
    }

    return (
      variants.find((variant) => variant.options.every((option, index) => option === selectedOptions[index])) ||
      variants.find((variant) => variant.available) ||
      variants[0]
    );
  }

  #getVariants(item) {
    const script = item.querySelector('[data-look-bundle-variants]');
    if (!script?.textContent) return [];

    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.error('Unable to parse bundle variants', error);
      return [];
    }
  }

  #currentProductVariantId() {
    const formId = this.dataset.currentFormId;
    const form = formId ? document.getElementById(formId) : document.querySelector('product-form-component form');
    return form?.querySelector('input[name="id"]')?.value;
  }

  #updateTotals() {
    let originalTotal = 0;
    let currentTotal = 0;
    let allAvailable = true;

    this.#items.forEach((item) => {
      const price = Number(item.dataset.price || 0);
      const comparePrice = Number(item.dataset.comparePrice || price);
      originalTotal += Math.max(comparePrice, price);
      currentTotal += price;
      allAvailable = allAvailable && item.dataset.available === 'true';
    });

    const discountPercent = Number(this.dataset.bundleDiscountPercent || 0);
    const discountedTotal = Math.round(currentTotal * (100 - discountPercent) / 100);
    const displayOriginal = Math.max(originalTotal, currentTotal);
    const savings = Math.max(displayOriginal - discountedTotal, 0);

    this.querySelectorAll('[data-look-bundle-total-original]').forEach((element) => {
      element.textContent = this.#formatMoney(displayOriginal);
      element.hidden = savings <= 0;
    });

    this.querySelectorAll('[data-look-bundle-total]').forEach((element) => {
      element.textContent = this.#formatMoney(discountedTotal);
    });

    this.querySelectorAll('[data-look-bundle-savings]').forEach((element) => {
      element.textContent = this.#formatMoney(savings);
      element.hidden = savings <= 0;
    });

    const addButton = this.querySelector('[data-look-bundle-add]');
    if (addButton) {
      addButton.disabled = !allAvailable || this.#items.length < 2;
    }
  }

  #handleAdd = async (event) => {
    event.preventDefault();

    const button = event.currentTarget;
    const items = this.#items
      .map((item) => ({
        id: Number(item.dataset.selectedVariantId),
        quantity: 1,
      }))
      .filter((item) => item.id);

    if (!items.length) return;

    button.disabled = true;

    const sections = Array.from(document.querySelectorAll('cart-items-component'))
      .map((item) => item.dataset.sectionId)
      .filter(Boolean)
      .join(',');

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ items, sections }),
      });
      const data = await response.json();

      if (data.status) {
        this.dispatchEvent(new CartErrorEvent(this.id, data.message, data.description, data.errors));
        this.#setStatus(data.message);
        return;
      }

      const cart = await fetch('/cart.js').then((cartResponse) => cartResponse.json());
      this.dispatchEvent(
        new CartAddEvent(cart, this.id, {
          source: 'look-bundle',
          itemCount: items.length,
          productId: this.dataset.productId,
          sections: data.sections,
        })
      );
      this.#setStatus(this.dataset.addedText || 'Added');
    } catch (error) {
      console.error(error);
      this.#setStatus(this.dataset.errorText || 'Unable to add bundle to cart.');
    } finally {
      this.#updateTotals();
    }
  };

  #setStatus(message) {
    const status = this.querySelector('[data-look-bundle-status]');
    if (status) status.textContent = message;
  }

  #formatMoney(value) {
    return formatMoney(value, this.dataset.moneyFormat || '${{amount}}', this.dataset.currency || 'USD');
  }
}

if (!customElements.get('look-bundle')) {
  customElements.define('look-bundle', LookBundle);
}
